import { NavLink, useLocation } from "react-router-dom";
import { MENU } from "../constants";
import { ChevronRightIcon, ChevronLeftIcon } from "@heroicons/react/outline";
import { LogoutIcon } from "@heroicons/react/outline";
import { useAuthStore } from "../store/auth.store";
import { useNavigate } from "react-router-dom";

const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();

  const logout = useAuthStore((state) => state.logout);
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const navigate = useNavigate();

  const visibleMenus = MENU.map((menu) => {
    // filter submenus by permission
    const visibleChildren = menu.children?.filter(
      (child) => !child.permission || hasPermission(child.permission)
    );

    // parent is visible only if:
    // - it has visible children OR
    // - it has no children but has permission itself
    const hasAccess =
      visibleChildren?.length > 0 ||
      (!menu.children?.length &&
        (!menu.permission || hasPermission(menu.permission)));

    if (!hasAccess) return null;

    return {
      ...menu,
      children: visibleChildren,
    };
  }).filter(Boolean);

  return (
    <aside
      className={`fixed left-0 top-0 h-screen overflow-hidden bg-slate-900 border-r border-white/10
      transition-all duration-300
      ${collapsed ? "w-16" : "w-64"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 h-16">
        {!collapsed && (
          <span className="text-xs text-slate-400 uppercase tracking-[0.16em]">
            Afrodise Dashboard
          </span>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="text-slate-300 hover:text-white"
        >
          {collapsed ? (
            <ChevronRightIcon className="w-6 h-6" />
          ) : (
            <ChevronLeftIcon className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Menu */}
      <nav className="px-2 space-y-3 mt-4">
        {visibleMenus.map((menu) => {
          const MenuIcon = menu.icon;

          if (menu.permission && !hasPermission(menu.permission)) {
            return null;
          }

          // Menu with children
          if (menu.children) {
            const isParentActive = menu.children.some((child) =>
              location.pathname.startsWith(child.path)
            );

            return (
              <div key={menu.label}>
                {/* Parent */}
                <div
                  className={`flex items-center gap-3 px-3 py-3 rounded-md
          ${isParentActive ? "bg-pink-600 text-white" : "text-slate-300"}`}
                >
                  <MenuIcon className="w-5 h-5 shrink-0" />
                  {!collapsed && (
                    <span className="text-xs uppercase tracking-wider">
                      {menu.label}
                    </span>
                  )}
                </div>

                {!collapsed &&
                  menu.children.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        `block px-8 py-2 rounded-md text-xs uppercase transition
                ${
                  isActive
                    ? "bg-slate-800 text-white"
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
              </div>
            );
          }

          // Menu without children (direct link)
          return (
            <NavLink
              key={menu.label}
              to={menu.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md transition
        ${
          isActive
            ? "bg-pink-600 text-white"
            : "text-slate-300 hover:bg-slate-800 hover:text-white"
        }`
              }
            >
              <MenuIcon className="w-5 h-5 shrink-0" />
              {!collapsed && (
                <span className="text-xs uppercase tracking-wider">
                  {menu.label}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {visibleMenus.length === 0 && (
        <div className="px-4 mt-6 text-xs text-slate-400">
          No accessible sections
        </div>
      )}

      {/* Logout */}
      <div className="absolute bottom-0 w-full p-2 border-t border-white/10">
        <button
          onClick={() => {
            logout();
            navigate("/", { replace: true });
          }}
          className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-300 rounded-md hover:bg-red-500/10 hover:text-red-400 transition"
        >
          <LogoutIcon className="w-5 h-5 shrink-0" />

          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
